import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Download, Flame, Cog, Wrench, Hammer, ShieldCheck, Ruler, Microscope } from "lucide-react";
import { useCategories, useContent, useProducts, useSettings, fileUrl } from "@/lib/api";
import { CountUp, ProductCard, Reveal, SectionHead } from "@/components/site/common";
import { track, useSeo } from "@/lib/seo";

export function Stats({ s }) {
  const st = s?.stats || {};
  const items = [[st.years || 40, "+", "Years of Heritage"], [st.export_years || 12, "+", "Years as Tijiya Exports"], [st.products || 46, "+", "Products"], [st.countries || 2, "", "Countries Served"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 border-t border-l border-[var(--stone)]">
      {items.map(([v, suf, l], i) => (
        <div key={l} className="border-r border-b border-[var(--stone)] p-8">
          <p className="serif text-5xl lg:text-6xl text-[var(--brass)]"><CountUp to={+v} suffix={suf} testid={`stat-${i}`} /></p>
          <p className="mt-3 text-sm text-[var(--graphite)]">{l}</p>
        </div>
      ))}
      <div className="border-r border-b border-[var(--stone)] p-8">
        <p className="serif text-5xl lg:text-6xl text-[var(--brass)]">{st.capacity ? <CountUp to={+st.capacity} testid="stat-capacity" /> : <span data-testid="stat-capacity" className="text-3xl">[TBC]</span>}</p>
        <p className="mt-3 text-sm text-[var(--graphite)]">MT Annual Capacity</p>
      </div>
    </div>
  );
}

const CAPS = [
  [Flame, "Hot-Dip Galvanising & Electroplating", "In-house zinc lines for HDG and bright zinc finishes.", "Coating thickness checked batch by batch."],
  [Cog, "Pressed Steel Machine Shop", "Power presses for arms, clamps, bands and caps.", "Tooling made and maintained on site."],
  [Wrench, "High-Intensity Threading", "Rolled and cut threads on rods, bolts and lag screws.", "Thread gauges at every station."],
  [Hammer, "Hydraulic Press & Forming", "Bending, hooking and forming of bolts and stakes.", "Repeatable bends to drawing."],
];

function Hero() {
  return (
    <section data-testid="home-hero" className="relative min-h-[88vh] flex items-end overflow-hidden">
      <img src="/img/factory.jpg" alt="Steel manufacturing floor" className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
      <div className="absolute inset-0 bg-[rgba(31,30,28,0.25)]" />
      <div className="wrap relative w-full pb-14 lg:pb-20 pt-32">
        <Reveal className="bg-[rgba(247,244,238,0.96)] backdrop-blur-sm border border-[var(--stone)] max-w-3xl p-8 sm:p-12">
          <p className="eyebrow mb-6">Since 1987 · Manufacturer & Exporter · Kolkata, India</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl">Precision-made fencing & construction hardware, trusted across continents.</h1>
          <p className="mt-6 text-[var(--graphite)] text-base md:text-lg">Fence fittings, foundation bolts, construction stakes, washers, cut rebars and threaded rods — engineered to specification and shipped worldwide.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/products" data-testid="hero-explore-products" className="btn btn-brass">Explore Products <ArrowRight size={16} /></Link>
            <Link to="/enquiry" data-testid="hero-request-quote" className="btn btn-outline">Request a Quote</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TrustStrip({ s }) {
  const items = ["40 Years of Manufacturing", "Govt. of India Recognised Export House", "In-house Hot-Dip Galvanising", "ASTM-grade Threaded Rods", `Exporting to ${(s?.countries || ["India", "USA"]).join(" & ")}`];
  return (
    <div data-testid="trust-strip" className="border-b border-[var(--stone)] bg-[var(--paper)]">
      <div className="wrap flex gap-8 overflow-x-auto no-scrollbar py-5 text-[13px] tracking-wide text-[var(--graphite)]">
        {items.map((t, i) => <span key={t} className="flex items-center gap-8 whitespace-nowrap">{i > 0 && <span className="w-1 h-1 rounded-full bg-[var(--brass)]" />}{t}</span>)}
      </div>
    </div>
  );
}

function Categories() {
  const { data: cats = [] } = useCategories();
  return (
    <section className="wrap py-24">
      <SectionHead num="01" eyebrow="Product range" title="Three families of steel hardware" sub="Every range is made in our own plant, finished in-house and packed for export." />
      <div className="grid md:grid-cols-3 gap-6">
        {cats.map((c, i) => (
          <Reveal key={c.slug} delay={i * 120}>
            <Link to={`/products/${c.slug}`} data-testid={`home-category-${c.slug}`} className="group card block h-full hover:border-[var(--brass)] transition-colors duration-300">
              <div className="img-frame prod-bg aspect-[4/3] flex items-center justify-center border-b border-[var(--stone)] p-10"><img src={c.image} alt={c.name} className="max-h-full object-contain" /></div>
              <div className="p-7">
                <p className="text-xs text-[var(--steel)] tabular">{String(c.count).padStart(2, "0")} products</p>
                <h3 className="text-2xl lg:text-3xl mt-2">{c.name}</h3>
                <p className="mt-3 text-sm text-[var(--graphite)]">{c.tagline}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--brass)]">View range <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  const { data = [] } = useProducts({ featured: true });
  return (
    <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-24">
      <div className="wrap">
        <SectionHead num="02" eyebrow="Featured" title="From the catalogue" action={<Link to="/products/all" className="link text-sm">All products A–Z →</Link>} />
        <div data-testid="featured-carousel" className="flex gap-6 overflow-x-auto no-scrollbar snap-x pb-2">
          {data.map((p) => <div key={p.slug} className="snap-start shrink-0 w-[260px] md:w-[290px]"><ProductCard p={p} compact /></div>)}
        </div>
      </div>
    </section>
  );
}

function LegacyTeaser({ s }) {
  const y = s?.exports_year || 2013;
  return (
    <section className="wrap py-24 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal><div className="img-frame aspect-[4/5] border border-[var(--stone)]"><img src="/img/mill.jpg" alt="Heritage steel works" className="w-full h-full object-cover sepia" loading="lazy" /></div></Reveal>
      <Reveal delay={150}>
        <div className="flex items-center gap-4 mb-4"><span className="sec-num">03</span><span className="eyebrow">Our legacy</span></div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl">Four decades of manufacturing. Over a decade as Tijiya Exports.</h2>
        <p className="mt-6 text-[var(--graphite)] md:text-lg">Founded in 1987 as Tijiya Engineering, our family business has spent nearly forty years making steel fencing and construction hardware. For the last twelve years it has carried that expertise forward as Tijiya Exports — the same people, plant and standards, focused on export buyers.</p>
        <div className="mt-10 grid grid-cols-2 border-t border-[var(--stone)]">
          <div className="pt-6 pr-6 border-r border-[var(--stone)]"><p className="serif text-5xl text-[var(--brass)]">1987</p><p className="text-sm mt-2 text-[var(--graphite)]">Tijiya Engineering</p></div>
          <div className="pt-6 pl-6"><p className="serif text-5xl text-[var(--brass)]">{y}</p><p className="text-sm mt-2 text-[var(--graphite)]">Tijiya Exports</p></div>
        </div>
        <Link to="/about/legacy" data-testid="legacy-teaser-link" className="mt-10 inline-flex link">Our Story →</Link>
      </Reveal>
    </section>
  );
}

function Capability() {
  return (
    <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-24">
      <div className="wrap">
        <SectionHead num="05" eyebrow="Manufacturing" title="Capability under one roof" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-[var(--stone)]">
          {CAPS.map(([I, t, a, b], i) => (
            <Reveal key={t} delay={i * 100} className="border-r border-b border-[var(--stone)] p-8">
              <I size={26} strokeWidth={1.3} className="text-[var(--steel)]" />
              <h3 className="text-2xl mt-6">{t}</h3>
              <p className="mt-3 text-sm text-[var(--graphite)]">{a}<br />{b}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Quality() {
  const { data: certs = [] } = useContent("certifications");
  const checks = [[Microscope, "Material testing", "Chemical and mechanical checks on incoming steel."], [ShieldCheck, "Surface-quality control", "Coating weight and finish inspected per batch."], [Ruler, "Dimensional & fitting checks", "Gauges and fit-up tests against drawing."]];
  return (
    <section className="wrap py-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5"><SectionHead num="06" eyebrow="Quality promise" title="Checked before it ships" className="!mb-0" /></div>
      <div className="lg:col-span-7">
        <div className="divide-y divide-[var(--stone)] border-y border-[var(--stone)]">
          {checks.map(([I, t, d]) => <Reveal key={t} className="flex gap-6 py-6"><I size={22} strokeWidth={1.3} className="text-[var(--brass)] mt-1 shrink-0" /><div><h3 className="text-2xl">{t}</h3><p className="text-sm text-[var(--graphite)] mt-1">{d}</p></div></Reveal>)}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">{certs.map((c) => <span key={c.id} className="chip !text-xs !py-2 !px-4">{c.name}</span>)}</div>
      </div>
    </section>
  );
}

function CalcPromo() {
  return (
    <section className="bg-[var(--ink)] text-[var(--paper)]">
      <div className="wrap py-14 flex flex-col md:flex-row md:items-center gap-8 justify-between">
        <div className="flex items-center gap-6">
          <span className="w-14 h-14 border border-[var(--brass)] flex items-center justify-center shrink-0"><Calculator size={24} className="text-[var(--brass)]" /></span>
          <p className="serif text-2xl md:text-3xl">Estimate your shipment weight and container load in seconds.</p>
        </div>
        <Link to="/shipment-calculator" data-testid="calc-promo-button" className="btn btn-brass">Open Calculator →</Link>
      </div>
    </section>
  );
}

function Markets({ s }) {
  const list = s?.countries || ["India", "USA"];
  return (
    <section className="wrap py-24">
      <SectionHead num="07" eyebrow="Markets" title="Where our hardware goes" sub="Fence contractors, distributors and importers in the United States, and construction firms across India." />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[var(--stone)] border border-[var(--stone)]">
        {list.map((c) => <div key={c} className="bg-[var(--paper)] p-8 serif text-2xl">{c}</div>)}
        {Array.from({ length: Math.max(0, 6 - list.length) }).map((_, i) => <div key={i} className="bg-[var(--paper)] p-8 text-xs text-[var(--steel)] flex items-center">[Client logo]</div>)}
      </div>
    </section>
  );
}

function MediaHighlights() {
  const { data = [] } = useContent("media");
  return (
    <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-24">
      <div className="wrap">
        <SectionHead num="08" eyebrow="Media" title="News & highlights" action={<Link to="/media" className="link text-sm">All media →</Link>} />
        <div className="grid md:grid-cols-3 gap-8">
          {data.slice(0, 3).map((m, i) => (
            <Reveal key={m.id} delay={i * 100}>
              <Link to={`/media/${m.slug}`} data-testid={`home-media-${i}`} className="group block">
                <div className="img-frame aspect-[16/10] border border-[var(--stone)]"><img src={fileUrl(m.image)} alt="" loading="lazy" className="w-full h-full object-cover" /></div>
                <p className="mt-4 text-xs text-[var(--steel)] uppercase tracking-widest">{m.type} · {m.date}</p>
                <h3 className="text-2xl mt-2 group-hover:text-[var(--brass)] transition-colors">{m.title}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: s } = useSettings();
  useSeo(null, null, { "@context": "https://schema.org", "@type": "Organization", name: "Tijiya Exports Private Limited", alternateName: "Tijiya Engineering", foundingDate: "1987", url: window.location.origin, logo: `${window.location.origin}/logo.png`, address: { "@type": "PostalAddress", addressLocality: "Kolkata", postalCode: "700001", addressCountry: "IN" } });
  return (
    <div data-testid="home-page">
      <Hero />
      <TrustStrip s={s} />
      <Categories />
      <Featured />
      <LegacyTeaser s={s} />
      <section className="wrap pb-24"><SectionHead num="04" eyebrow="By the numbers" title="Measured in decades" /><Stats s={s} /></section>
      <Capability />
      <Quality />
      <CalcPromo />
      <Markets s={s} />
      <MediaHighlights />
      <section className="wrap py-20 flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-[var(--stone)]">
        <div><p className="eyebrow mb-3">Catalogue</p><p className="serif text-3xl">Download our full product catalogue</p></div>
        {s?.catalogue_url ? <a href={fileUrl(s.catalogue_url)} target="_blank" rel="noreferrer" onClick={() => track("catalogue_download")} data-testid="home-catalogue-download" className="btn btn-outline"><Download size={16} /> Download PDF</a>
          : <Link to="/contact" data-testid="home-catalogue-download" className="btn btn-outline"><Download size={16} /> Request the PDF</Link>}
      </section>
      <section className="wrap py-28 text-left">
        <Reveal className="max-w-3xl">
          <p className="eyebrow mb-5">Made to drawing</p>
          <h2 className="text-4xl sm:text-5xl">Have a specification in mind? Send us your drawing or requirements.</h2>
          <Link to="/enquiry" data-testid="closing-cta" className="btn btn-brass mt-10">Start an enquiry <ArrowRight size={16} /></Link>
        </Reveal>
      </section>
    </div>
  );
}
